import {
  ChevronRight, TrendingUp, TrendingDown, Minus, Activity, Sparkles, Clock, MessageCircleQuestion,
} from "lucide-react";

export function todayISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}
export function parseHM(s) {
  const [h, m] = (s || "0:0").split(":").map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
}
export function nowHM() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

/** Format seconds → compact "1h 23m" / "45m" / "12s". Mirrors AdminAudit. */
export function fmtMin(secs) {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

export function ord(n) {
  const v = parseInt(n, 10) % 100;
  return ["th", "st", "nd", "rd"][(v - 20) % 10] || ["th", "st", "nd", "rd"][v] || "th";
}

export function DashCard({ children, onClick, testid, tone, icon: Icon, title, subtitle }) {
  const tones = {
    sky: "border-sky-200 dark:border-sky-800 hover:border-sky-300",
    amber: "border-amber-200 dark:border-amber-800 hover:border-amber-300",
    violet: "border-violet-200 dark:border-violet-800 hover:border-violet-300",
    emerald: "border-emerald-200 dark:border-emerald-800 hover:border-emerald-300",
    rose: "border-rose-200 dark:border-rose-800 hover:border-rose-300",
    pink: "border-pink-200 dark:border-pink-800 hover:border-pink-300",
    slate: "border-slate-200 dark:border-slate-700 hover:border-slate-300",
  };
  const iconTone = {
    sky: "bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300",
    amber: "bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300",
    violet: "bg-violet-50 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300",
    emerald: "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300",
    rose: "bg-rose-50 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300",
    pink: "bg-pink-50 dark:bg-pink-900/40 text-pink-600 dark:text-pink-300",
    slate: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      data-testid={testid}
      className={`cursor-pointer text-left bg-white dark:bg-slate-800 rounded-2xl border p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${tones[tone]}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconTone[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{title}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">{subtitle}</div>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
      <div>{children}</div>
    </div>
  );
}

export function SessionPill({ label, icon: Icon, session }) {
  if (!session?.submitted) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 text-[11px] font-medium">
        <Icon className="h-3 w-3" /> {label}: not marked
      </span>
    );
  }
  const tone = session.percentage >= 90 ? "emerald" : session.percentage >= 75 ? "sky" : "rose";
  const cls = {
    emerald: "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
    sky: "bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300",
    rose: "bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" /> {label}: {session.percentage}%
    </span>
  );
}

export function Pill({ tone, icon: Icon, label }) {
  const cls = {
    rose: "bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300",
    pink: "bg-pink-50 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export function Skel() {
  return <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" />;
}

export function TrendBadge({ trend, prev }) {
  if (trend === "up") return <span className="text-emerald-600 dark:text-emerald-300" title={prev != null ? `Up from ${prev}%` : "Improved"}><TrendingUp className="h-3.5 w-3.5" /></span>;
  if (trend === "down") return <span className="text-rose-500" title={prev != null ? `Down from ${prev}%` : "Dropped"}><TrendingDown className="h-3.5 w-3.5" /></span>;
  if (trend === "same") return <span className="text-slate-400" title="No change"><Minus className="h-3.5 w-3.5" /></span>;
  if (trend === "new") return <span className="text-[10px] uppercase tracking-wide text-sky-500" title="No previous exam">new</span>;
  return <span className="w-3.5" />;
}

export function TutorAuditTile({ tutorTop, classLabel, sec, onViewAll }) {
  return (
    <div className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid="dashboard-tutor-audit-tile">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300">
            <Activity className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Your students this week</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">AI Tutor activity — last 7 days</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs font-semibold text-sky-600 dark:text-sky-300 hover:text-sky-700 dark:hover:text-sky-200 inline-flex items-center gap-1"
          data-testid="dashboard-tutor-audit-view-all"
        >
          View full audit <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {!tutorTop ? (
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-slate-100 dark:bg-slate-700/40 animate-pulse" />)}
        </div>
      ) : (tutorTop.students || []).length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center" data-testid="dashboard-tutor-audit-empty">
          No one from {classLabel}-{sec} has opened the AI Tutor this week yet. Encourage them in class!
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dashboard-tutor-audit-top3">
            {(tutorTop.students || []).slice(0, 3).map((s, i) => (
              <div key={s.student_id} className="rounded-xl border border-slate-100 dark:border-slate-700 bg-gradient-to-br from-white to-sky-50/40 dark:from-slate-900 dark:to-sky-900/10 p-3.5">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-sky-500">
                    {["🥇 Most engaged", "🥈 Runner-up", "🥉 Third"][i]}
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">{s.roll_number}</div>
                </div>
                <div className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate" style={{ fontFamily: "Outfit" }}>
                  {s.student_name}
                </div>
                <div className="mt-2 flex items-center gap-3 text-[11px]">
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300 font-semibold">
                    <Clock className="h-3 w-3" /> {fmtMin(s.total_seconds)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-violet-600 dark:text-violet-300 font-semibold">
                    <MessageCircleQuestion className="h-3 w-3" /> {s.total_questions} q
                  </span>
                </div>
                {s.subjects?.[0] && (
                  <div className="mt-1.5 text-[10px] text-slate-500 dark:text-slate-400 truncate">
                    Top: <span className="font-medium text-slate-700 dark:text-slate-200">{s.subjects[0].subject_name}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-sky-500" /> {tutorTop.totals?.students_active || 0} active</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-emerald-500" /> {fmtMin(tutorTop.totals?.total_seconds || 0)} total</span>
            <span className="inline-flex items-center gap-1"><MessageCircleQuestion className="h-3 w-3 text-violet-500" /> {tutorTop.totals?.total_questions || 0} questions</span>
          </div>
        </>
      )}
    </div>
  );
}
